import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

const OUTPUT_SIZE = 512;
const DEFAULT_CROP_ZOOM = 1;
const MIN_CROP_ZOOM = 0.25;
const MAX_CROP_ZOOM = 4;

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const normalizePan = (pan, imageInfo, frameSize, zoom) => {
  if (!imageInfo || !frameSize) return { x: 0, y: 0 };
  const coverScale = Math.max(frameSize / imageInfo.width, frameSize / imageInfo.height);
  const drawWidth = imageInfo.width * coverScale * zoom;
  const drawHeight = imageInfo.height * coverScale * zoom;
  const maxPanX = Math.max(0, (drawWidth - frameSize) / 2);
  const maxPanY = Math.max(0, (drawHeight - frameSize) / 2);
  return {
    x: clamp(pan.x, -maxPanX, maxPanX),
    y: clamp(pan.y, -maxPanY, maxPanY),
  };
};

const ImageImportOverlay = ({
  isOpen,
  onConfirm,
  onCancel,
  lockedMode = null,
}) => {
  const fileInputRef = useRef(null);
  const frameRef = useRef(null);
  const [imageSrc, setImageSrc] = useState(null);
  const [imageInfo, setImageInfo] = useState(null);
  const [mode, setMode] = useState('original'); // 'original', 'square', 'circle'
  const [zoom, setZoom] = useState(DEFAULT_CROP_ZOOM);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [dragState, setDragState] = useState(null);
  const activeLockedMode = lockedMode === 'original' || lockedMode === 'square' || lockedMode === 'circle'
    ? lockedMode
    : null;
  const isReplacementMode = Boolean(activeLockedMode);

  useEffect(() => {
    if (!isOpen) {
      setImageSrc(null);
      setImageInfo(null);
      setMode(activeLockedMode || 'original');
      setZoom(DEFAULT_CROP_ZOOM);
      setPan({ x: 0, y: 0 });
    }
  }, [activeLockedMode, isOpen]);

  useEffect(() => {
    if (!isOpen || !activeLockedMode) return;
    if (mode !== activeLockedMode) {
      setMode(activeLockedMode);
      setZoom(DEFAULT_CROP_ZOOM);
      setPan({ x: 0, y: 0 });
    }
  }, [activeLockedMode, isOpen, mode]);

  useEffect(() => {
    if (!imageSrc) {
      setImageInfo(null);
      return undefined;
    }
    let mounted = true;
    const image = new Image();
    image.onload = () => {
      if (!mounted) return;
      setImageInfo({ image, width: image.naturalWidth, height: image.naturalHeight });
    };
    image.src = imageSrc;
    return () => {
      mounted = false;
    };
  }, [imageSrc]);

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      setImageSrc(e.target.result);
      setZoom(DEFAULT_CROP_ZOOM);
      setPan({ x: 0, y: 0 });
    };
    reader.readAsDataURL(file);
    event.target.value = '';
  };

  const frameSize = frameRef.current?.getBoundingClientRect().width || 320;
  const normalizedPan = useMemo(
    () => normalizePan(pan, imageInfo, frameSize, zoom),
    [frameSize, imageInfo, pan, zoom]
  );

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') onCancel();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen, onCancel]);

  useEffect(() => {
    if (!dragState || mode === 'original') return undefined;
    const onPointerMove = (event) => {
      if (event.pointerId !== dragState.pointerId) return;
      const nextPan = normalizePan(
        {
          x: dragState.startPan.x + (event.clientX - dragState.startX),
          y: dragState.startPan.y + (event.clientY - dragState.startY),
        },
        imageInfo,
        frameSize,
        zoom
      );
      setPan(nextPan);
    };
    const onPointerUp = (event) => {
      if (event.pointerId !== dragState.pointerId) return;
      setDragState(null);
    };
    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);
    window.addEventListener('pointercancel', onPointerUp);
    return () => {
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
      window.removeEventListener('pointercancel', onPointerUp);
    };
  }, [dragState, frameSize, imageInfo, mode, zoom]);

  const handlePointerDown = (event) => {
    if (!imageInfo || mode === 'original') return;
    setDragState({
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      startPan: normalizedPan,
    });
    frameRef.current?.setPointerCapture?.(event.pointerId);
  };

  const transformStyle = useMemo(() => {
    if (!imageInfo) return {};

    if (mode === 'original') {
      return {
        maxWidth: '100%',
        maxHeight: '100%',
        objectFit: 'contain',
        left: '50%',
        top: '50%',
        transform: 'translate(-50%, -50%)',
      };
    }

    const coverScale = Math.max(frameSize / imageInfo.width, frameSize / imageInfo.height);
    const drawWidth = imageInfo.width * coverScale * zoom;
    const drawHeight = imageInfo.height * coverScale * zoom;
    return {
      width: `${drawWidth}px`,
      height: `${drawHeight}px`,
      transform: `translate(-50%, -50%) translate(${normalizedPan.x}px, ${normalizedPan.y}px)`,
    };
  }, [frameSize, imageInfo, mode, normalizedPan.x, normalizedPan.y, zoom]);

  const handleConfirm = useCallback(() => {
    if (!imageInfo) return;

    if (mode === 'original') {
      onConfirm({
        imageData: imageSrc,
        imageCropMode: 'original',
        outputWidth: imageInfo.width,
        outputHeight: imageInfo.height,
      });
      return;
    }

    const canvas = document.createElement('canvas');
    canvas.width = OUTPUT_SIZE;
    canvas.height = OUTPUT_SIZE;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const sourceFrameSize = frameRef.current?.getBoundingClientRect().width || 320;
    const coverScale = Math.max(sourceFrameSize / imageInfo.width, sourceFrameSize / imageInfo.height);
    const drawWidth = imageInfo.width * coverScale * zoom;
    const drawHeight = imageInfo.height * coverScale * zoom;
    const ratio = OUTPUT_SIZE / sourceFrameSize;
    const outputDrawWidth = drawWidth * ratio;
    const outputDrawHeight = drawHeight * ratio;
    const outputX = (OUTPUT_SIZE - outputDrawWidth) / 2 + normalizedPan.x * ratio;
    const outputY = (OUTPUT_SIZE - outputDrawHeight) / 2 + normalizedPan.y * ratio;

    ctx.clearRect(0, 0, OUTPUT_SIZE, OUTPUT_SIZE);

    if (mode === 'circle') {
      ctx.beginPath();
      ctx.arc(OUTPUT_SIZE / 2, OUTPUT_SIZE / 2, OUTPUT_SIZE / 2, 0, Math.PI * 2);
      ctx.clip();
    }

    ctx.drawImage(
      imageInfo.image,
      outputX,
      outputY,
      outputDrawWidth,
      outputDrawHeight
    );

    onConfirm({
      imageData: canvas.toDataURL('image/png'),
      imageCropMode: mode,
      outputWidth: OUTPUT_SIZE,
      outputHeight: OUTPUT_SIZE,
    });
  }, [imageInfo, imageSrc, mode, normalizedPan.x, normalizedPan.y, onConfirm, zoom]);

  if (!isOpen) return null;

  if (!imageSrc) {
    return (
      <div className="crop-overlay-backdrop" role="dialog" aria-modal="true" aria-label="Import Image">
        <div className="crop-overlay-modal" style={{ maxWidth: 400 }}>
          <div className="crop-overlay-header">
            <h3>{isReplacementMode ? 'Replace Image' : 'Import Image'}</h3>
            <p>{isReplacementMode ? 'Select an image to replace the current image layer.' : 'Select an image to add as a new layer.'}</p>
          </div>
          <div className="crop-overlay-actions" style={{ justifyContent: 'center', padding: '40px 0' }}>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={handleFileChange}
            />
            <button type="button" className="preset-action-btn" onClick={() => fileInputRef.current?.click()}>
              Choose File
            </button>
            <button type="button" className="secondary-btn" onClick={onCancel}>Cancel</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="crop-overlay-backdrop" role="dialog" aria-modal="true" aria-label="Configure Image">
      <div className="crop-overlay-modal">
        <div className="crop-overlay-header">
          <h3>{isReplacementMode ? 'Replace Image' : 'Configure Image'}</h3>
          <p>
            {isReplacementMode
              ? `Crop shape is locked to ${activeLockedMode}. Adjust the crop content if needed.`
              : 'Choose import mode and adjust crop if needed.'}
          </p>
        </div>

        {!isReplacementMode && (
          <div className="image-import-modes" style={{ display: 'flex', gap: '8px', marginBottom: '16px', justifyContent: 'center' }}>
            {['original', 'square', 'circle'].map(m => (
              <button
                key={m}
                type="button"
                className={`secondary-btn ${mode === m ? 'is-active' : ''}`}
                onClick={() => {
                  setMode(m);
                  setZoom(DEFAULT_CROP_ZOOM);
                  setPan({ x: 0, y: 0 });
                }}
                style={mode === m ? { borderColor: '#60a5fa', background: 'rgba(37,99,235,0.18)' } : {}}
              >
                {m.charAt(0).toUpperCase() + m.slice(1)}
              </button>
            ))}
          </div>
        )}

        <div
          className="crop-frame"
          ref={frameRef}
          onPointerDown={handlePointerDown}
          role="presentation"
          style={{ cursor: mode === 'original' ? 'default' : 'move' }}
        >
          {imageInfo && (
            <img
              src={imageSrc}
              alt="Crop preview"
              className="crop-image"
              draggable={false}
              style={transformStyle}
            />
          )}
          {mode === 'circle' && <div className="crop-frame-circle-mask" aria-hidden="true" />}
          {mode !== 'original' && (
            <div
              className="crop-frame-guide"
              style={mode === 'circle' ? { borderRadius: '50%' } : {}}
            />
          )}
        </div>

        {mode !== 'original' && (
          <div className="crop-controls">
            <label htmlFor="crop-zoom">Zoom</label>
            <input
              id="crop-zoom"
              type="range"
              min={MIN_CROP_ZOOM}
              max={MAX_CROP_ZOOM}
              step={0.01}
              value={zoom}
              onChange={(event) => setZoom(Number(event.target.value))}
            />
            <span>{Math.round(zoom * 100)}%</span>
          </div>
        )}

        <div className="crop-overlay-actions">
          <button type="button" className="secondary-btn" onClick={() => setImageSrc(null)}>Back</button>
          <button type="button" className="secondary-btn" onClick={handleConfirm}>Import</button>
        </div>
      </div>
    </div>
  );
};

export default ImageImportOverlay;
